import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {FORM_DEFINITION_FILE_STATE_QUERY} from 'forms/form-definition/FormDefinitionQueries';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';

import {
  FormDefinitionFileStateQuery,
  FormDefinitionFileStateQueryVariables,
} from './__generated/FormDefinitionFileStateQuery';
import FormDefinitionTabState from './FormDefinitionTabState';

type FormDefinition = FormDefinitionFileStateQuery['formDefinition'];

interface FormDefinitionFileStateAttributes {
  formDefinition?: FormDefinition;
  editUrl?: string;
  name?: string;
  archiveRecord?: ArchiveRecord | null;
}

class FormDefinitionFileState extends ApolloFileState<
  FormDefinitionFileStateQuery,
  FormDefinitionFileStateQueryVariables,
  FormDefinitionFileStateAttributes
> {
  static fileType = ModelTypeConstants.FORM_DEFINITION;
  static override getQueryOptions(fileId: string): QueryOptions<FormDefinitionFileStateQueryVariables> {
    return {
      query: FORM_DEFINITION_FILE_STATE_QUERY,
      variables: {
        formDefinitionId: fileId,
      },
    };
  }

  static override dataToAttributes(data: FormDefinitionFileStateQuery): FormDefinitionFileStateAttributes {
    const {formDefinition} = data;
    return isInaccessible(formDefinition)
      ? {}
      : {
          formDefinition,
          editUrl: formDefinition.editUrl,
          name: formDefinition.name,
          archiveRecord: formDefinition.archiveRecord,
        };
  }

  static override openFile(fileId: string): JQueryPromise<FormDefinitionFileState> {
    return super.openFile(fileId) as JQueryPromise<FormDefinitionFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }
  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers()
      .at(0)
      .tabs()
      .add(
        new FormDefinitionTabState({
          formDefinitionId: this.id,
        })
      );

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  override editURL(): string | undefined {
    return this.get('editUrl');
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set('archiveRecord', archiveRecord);
  }

  // This is present so that when a form definition is archived due to its project being archived, an error
  // isn't thrown due to setPermissions being absent. However, we don't need to do anything here because the
  // FormDefinition view will poll if opened and fetch the updated permissions.
  override setPermissions(): void {}

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }
}

export default FormDefinitionFileState;
